import { FC, JSX } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Typography,
} from '@mui/joy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Accordian } from '@consolidocs-ui/components';

interface SidebarItemType {
  label: string;
  path?: string;
  icon?: JSX.Element;
  children?: SidebarItemType[];
  visible?: boolean;
}

interface SidebarItemProps {
  item: SidebarItemType;
  parentPath?: string;
  onNavigate?: () => void;
}

const SidebarItem: FC<SidebarItemProps> = ({
  item,
  parentPath = '',
  onNavigate,
}): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  if (item.visible === false) {
    return <></>;
  }

  const fullPath = item.path?.startsWith('/')
    ? item.path
    : `${parentPath}/${item?.path ?? ''}`.replace(/\/+/g, '/');

  return (
    <ListItem nested={hasChildren}>
      {hasChildren ? (
        <Accordian
          defaultExpanded={true}
          renderToggle={({ open, setOpen }) => (
            <ListItemButton onClick={() => setOpen(!open)}>
              {item.icon}
              <ListItemContent>
                <Typography level="title-sm">{t(item.label)}</Typography>
              </ListItemContent>
              <KeyboardArrowDownIcon
                sx={open ? { transform: 'rotate(180deg)' } : {}}
              />
            </ListItemButton>
          )}
        >
          <List sx={{ gap: 0.5 }}>
            {item?.children?.map((child, index) => (
              <SidebarItem
                key={index}
                item={child}
                parentPath={fullPath}
                onNavigate={onNavigate}
              />
            ))}
          </List>
        </Accordian>
      ) : (
        <ListItemButton
          selected={fullPath === location.pathname}
          onClick={() => {
            if (fullPath) {
              navigate(fullPath);
              onNavigate?.();
            }
          }}
        >
          {item.icon}
          <ListItemContent>{t(item.label)}</ListItemContent>
        </ListItemButton>
      )}
    </ListItem>
  );
};

export default SidebarItem;
